import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * SupplierPicker — searchable supplier select backed by the "Suppliers" SharePoint list.
 *
 * Props:
 *   value        {string}   — current supplier name (display value)
 *   supplierId   {string}   — current supplier id
 *   onChange     {fn}       — called with ({ id, name }) when a supplier is chosen or cleared
 *   required     {boolean}
 *   disabled     {boolean}
 */
export const SupplierPicker = ({ value, supplierId, onChange, required, disabled }) => {
  const { service } = useApp();
  const { theme } = useTheme();
  const [query, setQuery] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const ref = useRef(null);

  // Load active suppliers from both local and external SRM sources
  useEffect(() => {
    if (initialised) return;
    setInitialised(true);
    setLoading(true);

    const loadSuppliers = async () => {
      try {
        // Fetch local suppliers
        const localData = await service.getListItems("Suppliers").catch(() => ({ value: [] }));
        const localSuppliers = (localData?.value || [])
          .map(i => ({ id: i.id, ...i.fields, source: "local" }))
          .filter(s => s.Status !== "Inactive");

        // Fetch external SRM suppliers
        const externalSiteUrl = "https://lekoillimited.sharepoint.com/sites/SRMDashboard";
        const srmData = await service.getListItemsFromExternalSite(externalSiteUrl, "Suppliers").catch(() => ({ value: [] }));
        const srmSuppliers = (srmData?.value || [])
          .map(i => ({ id: i.id, ...i.fields, source: "srm" }))
          .filter(s => s.Status !== "Inactive");

        // Merge: local first, then SRM (deduplicate by Title)
        const merged = [...localSuppliers];
        const localTitles = new Set(localSuppliers.map(s => (s.Title || "").toLowerCase()));
        srmSuppliers.forEach(s => {
          if (!localTitles.has((s.Title || "").toLowerCase())) {
            merged.push(s);
          }
        });

        // Sort by title
        merged.sort((a, b) => (a.Title || "").localeCompare(b.Title || ""));
        setSuppliers(merged);
      } catch (error) {
        console.error("Error loading suppliers:", error);
        setSuppliers([]);
      } finally {
        setLoading(false);
      }
    };

    loadSuppliers();
  }, [initialised, service]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query.trim()
    ? suppliers.filter(s => {
        const titleMatch = (s.Title || "").toLowerCase().includes(query.toLowerCase());
        const categoryMatch = (s.Category || "").toLowerCase().includes(query.toLowerCase());
        return titleMatch || categoryMatch;
      })
    : suppliers;

  const inputStyle = {
    width: "100%", padding: "10px 12px", background: theme.bg.secondary,
    border: `1px solid ${theme.border}`, borderRadius: 8, color: theme.text.primary,
    fontSize: 15, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", outline: "none",
  };

  const handleSelect = (s) => {
    onChange({ id: s.id, name: s.Title || "" });
    setQuery("");
    setOpen(false);
  };

  const handleClear = () => {
    onChange({ id: "", name: "" });
    setQuery("");
  };

  // Accept custom text (free-text entry not in the list)
  const handleCustomInput = () => {
    if (query.trim()) {
      onChange({ id: "", name: query.trim() }); // No ID for custom suppliers
      setQuery("");
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      {value ? (
        /* Selected state */
        <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-700 border border-yellow-600/30 rounded-lg">
          <div className="flex-1 overflow-hidden">
            <div className="text-sm text-slate-100 font-medium whitespace-nowrap overflow-hidden text-ellipsis">
              {value}
            </div>
            {supplierId && suppliers.find(s => s.id === supplierId)?.Category && (
              <div className="text-sm text-slate-400 mt-0.5">
                {suppliers.find(s => s.id === supplierId).Category}
              </div>
            )}
            {!supplierId && (
              <div className="text-xs text-slate-400 mt-0.5">
                📝 Custom supplier
              </div>
            )}
          </div>
          {!disabled && (
            <button type="button" onClick={handleClear}
              className="bg-transparent border-none text-slate-400 hover:text-slate-300 cursor-pointer text-lg p-0 leading-none transition-colors">
              ×
            </button>
          )}
        </div>
      ) : (
        /* Search state */
        <>
          <input
            autoComplete="off"
            spellCheck="false"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCustomInput();
              }
            }}
            placeholder={loading ? "Loading suppliers..." : "Search supplier name or type custom..."}
            disabled={disabled || loading}
            className={`w-full px-3 py-2.5 bg-slate-700 border rounded-lg text-slate-100 text-sm focus:outline-none transition-colors ${
              open ? "border-yellow-600 focus:ring-2 focus:ring-yellow-600/50" : "border-slate-600"
            } ${disabled || loading ? "opacity-50 cursor-not-allowed" : ""}`}
          />
          {open && (
            <div key={`dropdown-${query}-${filtered.length}`} className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg z-50 max-h-56 overflow-y-auto shadow-xl">
              {filtered.length === 0 ? (
                <div className="flex flex-col gap-1">
                  <div className="px-3.5 py-3 text-sm text-slate-400">
                    {query ? "No suppliers match your search." : "No active suppliers found."}
                  </div>
                  {query.trim() && (
                    <div
                      onMouseDown={handleCustomInput}
                      className="px-3.5 py-2.5 cursor-pointer border-t border-slate-700 flex items-center gap-2 hover:bg-slate-700/50 transition-colors text-sm text-yellow-400 font-medium">
                      <span>✏️ Use custom: "{query.trim()}"</span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {filtered.map(s => (
                    <div key={s.id}
                      onMouseDown={() => handleSelect(s)}
                      className="px-3.5 py-2.5 cursor-pointer border-b border-slate-700 flex items-center gap-2.5 last:border-b-0 hover:bg-slate-700/50 transition-colors">
                      <div className="flex-1 overflow-hidden">
                        <div className="text-sm text-slate-100 font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                          {s.Title}
                        </div>
                        {s.Category && <div className="text-sm text-slate-400 mt-0.5">{s.Category}</div>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {s.source && (
                          <span className={`text-xs px-2 py-1 rounded-full font-semibold whitespace-nowrap ${
                            s.source === "local"
                              ? "bg-blue-900/20 text-blue-400"
                              : "bg-purple-900/20 text-purple-400"
                          }`}>
                            {s.source === "local" ? "📱 Local" : "☁️ SRM"}
                          </span>
                        )}
                        {s.Status && (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-900/20 text-green-400 font-semibold">
                            {s.Status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {query.trim() && (
                    <div
                      onMouseDown={handleCustomInput}
                      className="px-3.5 py-2.5 cursor-pointer border-t border-slate-700 flex items-center gap-2 hover:bg-slate-700/50 transition-colors text-sm text-yellow-400 font-medium">
                      <span>✏️ Use custom: "{query.trim()}"</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
